import { createHelpArticle } from '../helpModel'

export const profileCollegeArticles = [
  createHelpArticle({
    id: 'profile', slug: 'edit-profile', title: 'Edit my profile', category: 'Profile & College',
    description: 'Update the profile fields available on Edit profile and save your changes.',
    keywords: ['profile', 'bio', 'edit', 'department', 'course', 'year'],
    aliases: ['change my profile', 'update profile information', 'edit bio', 'update my details'],
    steps: [
      { title: 'Open Edit profile', body: 'Go to your Profile and choose “Edit profile”.' },
      { title: 'Update available fields', body: 'Edit the profile fields shown, such as your name, course/department, year, skills, or links.' },
      { title: 'Save', body: 'Choose “Save” in the Edit profile header and wait for the save to finish.' },
    ],
    notes: ['Available profile fields can depend on your account role.'],
    troubleshooting: [{ problem: 'A profile field is missing.', solution: 'Only edit fields currently displayed for your account; do not assume every role has the same fields.' }],
    relatedArticles: ['photo', 'cover-photo', 'username-change', 'course-year'],
    searchPriority: 4,
  }),
  createHelpArticle({
    id: 'photo', slug: 'change-profile-photo', title: 'Change or remove my profile picture', category: 'Profile & College',
    description: 'Choose a different profile photo or remove the current avatar from Edit profile.',
    keywords: ['profile picture', 'photo', 'avatar', 'dp', 'image'],
    aliases: ['change dp', 'update profile photo', 'new profile image', 'remove avatar', 'profile pic'],
    steps: [
      { title: 'Open Edit profile', body: 'Go to Profile and select “Edit profile”.' },
      { title: 'Choose a profile photo action', body: 'Tap your current profile photo to choose another image, or use its remove option when available.' },
      { title: 'Select a photo', body: 'Choose an image from the available device picker and confirm the selection.' },
    ],
    notes: ['The photo is uploaded when chosen; saving other profile fields is a separate action.'],
    relatedArticles: ['profile', 'cover-photo'],
    searchPriority: 2,
  }),
  createHelpArticle({
    id: 'cover-photo', slug: 'change-cover-photo', title: 'Change or remove my cover photo', category: 'Profile & College',
    description: 'Edit the profile cover image from the top of the Edit profile screen.',
    keywords: ['cover photo', 'cover image', 'profile banner', 'remove cover'],
    aliases: ['change profile banner', 'update cover picture', 'delete cover photo'],
    steps: [
      { title: 'Open Edit profile', body: 'Go to your Profile and choose “Edit profile”.' },
      { title: 'Select the cover area', body: 'Choose the cover-photo area at the top and select an image from your device.' },
      { title: 'Remove if needed', body: 'If a cover is set, use “Remove cover photo” to clear it.' },
    ],
    notes: ['Cover-photo upload/removal is separate from the profile-field “Save” action.'],
    relatedArticles: ['profile', 'photo'],
  }),
  createHelpArticle({
    id: 'username-change', slug: 'change-my-username', title: 'Change my username', category: 'Profile & College',
    description: 'Edit the username field on Edit profile and save it with your profile changes.',
    keywords: ['username', 'handle', 'profile name', 'account name'],
    aliases: ['how do I change my username', 'change handle', 'edit @name', 'update my username'],
    steps: [
      { title: 'Open Edit profile', body: 'Go to Profile and select “Edit profile”.' },
      { title: 'Edit Username', body: 'Update the username field shown on the form. The app filters unsupported characters while you type.' },
      { title: 'Save and review any message', body: 'Choose “Save”. If the username is unavailable or does not pass validation, follow the message shown by Campnect.' },
    ],
    notes: ['Do not rely on an unverified username-change cooldown or claim that a specific handle is available before the app accepts it.'],
    relatedArticles: ['username-rules', 'profile'],
    searchPriority: 8,
  }),
  createHelpArticle({
    id: 'username-rules', slug: 'username-rules', title: 'Username rules', category: 'Profile & College',
    description: 'The signup and profile forms validate username length and reserved or unsuitable names.',
    keywords: ['username', 'handle', 'reserved', 'characters', 'validation'],
    aliases: ['username not allowed', 'username unavailable', 'username too short', 'username format'],
    body: ['Choose a username accepted by the field. Registration and Edit profile apply validation; if a name is reserved or rejected, the app will show an error.'],
    notes: ['The profile editor currently enforces a 5–30 character length for a changed username. Other server-side checks may also apply. No change cooldown is documented here.'],
    relatedArticles: ['username-change', 'profile'],
  }),
  createHelpArticle({
    id: 'course-year', slug: 'update-course-department-and-year', title: 'Update my course, department, or year', category: 'Profile & College',
    description: 'Edit the course/department and year fields available to your account in Edit profile.',
    keywords: ['course', 'department', 'year', 'semester', 'student', 'faculty'],
    aliases: ['change my major', 'update study program', 'edit academic year', 'course details'],
    steps: [
      { title: 'Open Edit profile', body: 'Go to Profile and select “Edit profile”.' },
      { title: 'Choose the available field', body: 'Use the Department / Course picker and year control when they are shown for your account.' },
      { title: 'Save the profile', body: 'Choose “Save” in the header.' },
    ],
    notes: ['Registration and profile fields vary by role; Edit profile only exposes course/year fields when applicable.'],
    relatedArticles: ['profile', 'signup-email'],
  }),
  createHelpArticle({
    id: 'skills-links', slug: 'add-skills-and-links', title: 'Add skills and links to my profile', category: 'Profile & College',
    description: 'The Edit profile form supports a short skills list and up to two profile links.',
    keywords: ['skills', 'links', 'social links', 'portfolio', 'profile'],
    aliases: ['add portfolio link', 'add interests', 'add website to profile', 'profile links'],
    steps: [
      { title: 'Open Edit profile', body: 'Go to Profile and choose “Edit profile”.' },
      { title: 'Add skills or links', body: 'Use the Skills and Links controls shown on the form. The current form allows up to four skills and two links.' },
      { title: 'Save', body: 'Choose “Save” in the header to save profile-field changes.' },
    ],
    relatedArticles: ['profile', 'username-change'],
  }),
  createHelpArticle({
    id: 'profile-visibility', slug: 'public-vs-private-profile', title: 'Public vs. private profile', category: 'Profile & College',
    description: 'Use the profile-visibility control in Settings to choose the account visibility option shown there.',
    keywords: ['public profile', 'private profile', 'visibility', 'follow requests'],
    aliases: ['make my profile private', 'who can see my profile', 'private account', 'public account'],
    steps: [
      { title: 'Open Settings', body: 'Go to Profile and open Settings.' },
      { title: 'Find profile visibility', body: 'Use the profile-visibility control on Settings/Privacy and select the option you want.' },
      { title: 'Review requests', body: 'If private visibility is enabled, review follow requests in the area where Campnect presents them.' },
    ],
    notes: ['Per-post audience choices and account profile visibility are separate controls.'],
    relatedArticles: ['privacy', 'post-audience', 'follow-requests'],
  }),
  createHelpArticle({
    id: 'signup-missing-college', slug: 'college-missing-during-signup', title: 'My college is missing during signup', category: 'Profile & College',
    description: 'Registration includes a college request link when your college is not listed.',
    keywords: ['college', 'university', 'missing', 'signup', 'request'],
    aliases: ['cannot find my college', 'add my university', 'college not listed', 'request a college'],
    steps: [
      { title: 'Search the college list', body: 'In registration, search by your university or college name and abbreviation.' },
      { title: 'Use the request link', body: 'If it is not listed, select “Request your college” on the signup screen.' },
    ],
    notes: ['The address behind that link is dedicated to college-list requests only; it is not a general support contact. The app does not show a request-status tracker.'],
    relatedArticles: ['signup-email', 'college-change'],
  }),
  createHelpArticle({
    id: 'college-change', slug: 'change-college-after-signup', title: 'Can I change my college after signup?', category: 'Profile & College',
    description: 'A self-service college-change control was not verified in the current Edit profile screen.',
    keywords: ['change college', 'university transfer', 'college profile', 'school'],
    aliases: ['update my college', 'switch universities', 'change campus on profile'],
    body: ['The current Edit profile form does not expose a college-change control. Do not change your email or profile fields expecting that to transfer your account to another college.'],
    notes: ['This article does not promise a manual account transfer or provide a general support address.'],
    relatedArticles: ['profile', 'signup-missing-college'],
  }),
  createHelpArticle({
    id: 'follow-requests', slug: 'private-profile-follow-requests', title: 'Private-profile follow requests', category: 'Profile & College',
    description: 'When a profile is private, a follow request stays pending until its owner accepts it.',
    keywords: ['private profile', 'follow request', 'pending follow', 'accept follow'],
    aliases: ['waiting for someone to accept my follow request', 'why is my follow pending', 'private account requests'],
    body: ['A request to follow a private profile remains pending until the profile owner accepts it. Until it is accepted, do not assume that the private profile’s restricted content is visible to you.'],
    notes: ['Whether a profile is private is controlled by its actual privacy setting. Help does not imply that a request has been accepted merely because it was sent.'],
    relatedArticles: ['profile-visibility', 'privacy', 'block'],
  }),
]